import i18next from "i18next";
import { Check } from "lucide-react";
import React from "react";
import { getI18n, useTranslation } from "react-i18next";
import { switchLocaleAction } from "../actions/switch-locale";
import fetchData from "../utils/api";
import { Config } from "../utils/config";
import Button from "./Button";
import FormError from "./FormError";
import ProgressBar from "./ProgressBar";
import SelectOrderingMethod from "./SelectOrderingMethod";
import { Dialog, DialogContent } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "./ui/select";
import { toast } from "./ui/use-toast";

const isSelectedTypeNotInOrderingMethodData = (
  selectedType: string | null,
  orderingMethodData: { label: string; value: string }[]
): boolean => {
  if (selectedType) {
    return !orderingMethodData.some((method) => method.value === selectedType);
  }
  return false;
};

export default function SelectLanguage({
  isOpen,
  setIsOpen,
  setOpen,
  open,
  authToken,
  setSelectedType,
  selectedType,
  restaurantDetail,
  setRestaurantMessage,
  restaurantMessage,
}) {
  const { t } = useTranslation();
  const currentLanguage = getI18n();
  const [showStep, setShowStep] = React.useState(1);
  const [deliveryPostalCodeValue, setDeliveryPostalCodeValue] =
    React.useState<any>(localStorage?.getItem("deliveryPostalCodeValue"));
  const [postalCodeError, setPostalCodeError] = React.useState("");

  const totalSteps = selectedType === "delivery" ? 3 : 2;

  const languageLabel = Config.env.app.languages.find(
    (r) => r.value === currentLanguage.language
  )?.label;

  const handleChangeLanguage = async ({ value }) => {
    i18next.changeLanguage(value);
    switchLocaleAction(value);
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.set("language", value);
    window.history.pushState(null, "", url.toString());
  };

  const validatePostalCode = () => {
    if (!deliveryPostalCodeValue.trim()) {
      setPostalCodeError(t("delivery_postal_code.postal_code_required"));
      return false;
    }
    // Add any additional postal code validation logic here
    setPostalCodeError("");
    return true;
  };

  const [isOrderingMethodOpen, setIsOrderingMethodOpen] = React.useState(
    selectedType === "" ? true : false
  );

  const [orderingMethodData, setOrderingMethodData] = React.useState<any>([]);
  const [isMethod, setIsMethod] = React.useState(
    selectedType === "dine_in" ||
      selectedType === "table_booking" ||
      selectedType === "table_reservation"
      ? false
      : true
  );

  React.useEffect(() => {
    setIsMethod(
      selectedType === "dine_in" ||
        selectedType === "table_booking" ||
        selectedType === "table_reservation"
        ? false
        : true
    );
  }, [selectedType]);

  React.useEffect(() => {
    setOrderingMethodData(
      Config.env.app.ordering_method_header.reduce((acc: any, item: any) => {
        if (
          (item.value === "table_reservation" &&
            !restaurantDetail.services.table_reservation) ||
          (item.value === "pickup" &&
            !restaurantDetail.services.pickup_service) ||
          (item.value === "delivery" &&
            !restaurantDetail.services.delivery_service) ||
          (item.value === "in_restaurant" &&
            (!restaurantDetail.services.table_reservation ||
              !restaurantDetail.tableReservation.allow_guest_pre_order)) ||
          item.value === "dine_in"
        ) {
          return acc;
        }
        return [...acc, item];
      }, [])
    );
  }, [restaurantDetail]);

  React.useEffect(() => {
    if (
      selectedType !== "dine_in" &&
      selectedType !== "table_booking" &&
      selectedType !== "table_reservation" &&
      orderingMethodData.length > 0
    ) {
      const result = isSelectedTypeNotInOrderingMethodData(
        selectedType,
        orderingMethodData
      );
      if (result === true) {
        setSelectedType("");
        const url = new URL(window.location.href);
        const params = url.searchParams;
        params.delete("type");
        window.history.pushState(null, "", url.toString());
        setIsOrderingMethodOpen(true);
      }
    }
  }, [orderingMethodData, selectedType]);

  const handleOrderMethod = async ({ method }) => {
    const cachedCart = localStorage.getItem("cart");
    try {
      const body: any = {
        cart_id: cachedCart,
        ordering_method: method,
        ...(method === "delivery" && { postal_code: deliveryPostalCodeValue }),
      };
      const res = await fetchData({
        url: "cart/change-ordering-method",
        method: "POST",
        body,
        token: authToken,
      });
    } catch (error) {}
  };

  const renderProgressSteps = () => {
    return (
      <div className="flex items-center justify-center gap-3">
        {[...Array(totalSteps)].map((_, index) => (
          <React.Fragment key={index}>
            <div className="space-y-2">
              <div
                className={`flex-shrink-0 size-[30px] flex items-center justify-center ${
                  showStep > index + 1
                    ? "bg-green-900"
                    : showStep === index + 1
                    ? "bg-green-900"
                    : "bg-transparent border border-green-900"
                } rounded-full mx-auto`}
              >
                {showStep > index + 1 ? (
                  <Check className="size-4 text-white" />
                ) : (
                  <p
                    className={`text-base font-semibold ${
                      showStep === index + 1 ? "text-white" : "text-green-900"
                    }`}
                  >
                    {index + 1}
                  </p>
                )}
              </div>
            </div>
            {index < totalSteps - 1 && (
              <ProgressBar
                progress={showStep > index + 1 ? 2 : 1}
                maxProgress={2}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStepLabels = () => {
    return (
      <div className="flex items-center justify-between">
        <p
          className={`${
            selectedType === "delivery" ? "ml-[-7%]s" : ""
          } text-green-900 text-sm font-bold whitespace-nowrap`}
        >
          {t("languages.title")}
        </p>
        <p
          className={`ml-[-6%]s text-green-900 text-sm font-bold whitespace-nowrap transition-ease ${
            showStep === 1 ? "opacity-60" : ""
          }`}
        >
          {t("cart.ordering_method.title")}
        </p>
        {selectedType === "delivery" && (
          <p
            className={`mr-[-5%]s text-green-900 text-sm font-bold whitespace-nowrap transition-ease ${
              showStep < 3 ? "opacity-60" : ""
            }`}
          >
            {t("delivery_postal_code.title")}
          </p>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (showStep) {
      case 1:
        return (
          <div className="space-y-2">
            <div className="space-y-1">
              <h3 className="text-base font-semibold leading-none tracking-tight">
                {t("languages.title")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("languages.placeholder")}
              </p>
            </div>

            <div className="w-full flex items-center gap-2">
              <Select
                onValueChange={(e: any) => {
                  handleChangeLanguage({ value: e });
                }}
                value={currentLanguage.language}
              >
                <SelectTrigger>
                  <div className="w-full flex items-center gap-2">
                    <button>
                      <img
                        src={
                          Config.env.app.languages.find(
                            (r) => r.value === currentLanguage.language
                          )?.image || Config.env.app.languages[0].image
                        }
                        height={20}
                        width={20}
                        alt={
                          Config.env.app.languages.find(
                            (r) => r.value === currentLanguage.language
                          )?.label || Config.env.app.languages[0].label
                        }
                      />
                    </button>
                    <Label className="w-full text-left text-muted-foreground hover:text-foreground">
                      {languageLabel ? t(languageLabel) : t("languages.title")}
                    </Label>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t("languages.title")}</SelectLabel>
                    {Config.env.app.languages.map((item, index) => (
                      <SelectItem
                        key={index}
                        value={item.value}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex gap-2">
                          <div className="flex-shrink-0">
                            <img
                              src={item.image}
                              height={20}
                              width={20}
                              alt={item.label}
                            />
                          </div>
                          <div className="flex-shrink-0">{t(item.label)}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 2:
        return (
          <SelectOrderingMethod
            handleOrderMethod={handleOrderMethod}
            orderingMethodData={orderingMethodData}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            open={open}
          />
        );
      case 3:
        return (
          <div className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-base font-semibold leading-none tracking-tight">
                {t("delivery_postal_code.title")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("delivery_postal_code.description")}
              </p>
            </div>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder={t("delivery_postal_code.placeholder")}
                value={deliveryPostalCodeValue}
                onChange={(e) => {
                  setDeliveryPostalCodeValue(e.target.value);
                  if (postalCodeError) setPostalCodeError("");
                }}
              />
              {postalCodeError && <FormError msg={postalCodeError} />}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      {!open && (
        <Dialog open={isOpen}>
          <DialogContent
            isCloseButtonHidden={true}
            className="sm:max-w-[600px] overflow-y-auto max-h-[calc(100vh-6rem)] p-0"
          >
            <div className="px-3 sm:px-16 pt-5 pb-4 bg-green">
              {selectedType === "table_reservation" ? (
                <div className="space-y-2">
                  <div className="flex-shrink-0 size-[30px] flex items-center justify-center bg-green-900 rounded-full mx-auto">
                    <p className="text-white text-base font-semibold">1</p>
                  </div>
                  <p className="text-green-900 text-sm font-bold whitespace-nowrap text-center">
                    {t("languages.title")}
                  </p>
                </div>
              ) : (
                <div className="max-w-[304px] mx-auto space-y-1">
                  {renderProgressSteps()}
                  {renderStepLabels()}
                </div>
              )}
            </div>

            <div className="w-full max-w-[342px] mx-auto py-16 px-4 space-y-2">
              {renderContent()}
            </div>

            <div className="p-5 flex items-center justify-between border-t">
              {selectedType === "table_reservation" ? (
                <Button
                  role="button"
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    setIsOpen(true);
                    setOpen(true);
                    setRestaurantMessage(
                      restaurantMessage[currentLanguage.language] !== ""
                        ? true
                        : false
                    );
                  }}
                  disabled={selectedType === ""}
                  label={t("apply")}
                  className="ml-auto"
                />
              ) : (
                <>
                  {showStep > 1 && (
                    <Button
                      role="button"
                      variant="outline"
                      size="lg"
                      onClick={() => setShowStep(showStep - 1)}
                      label={t("back")}
                    />
                  )}
                  <Button
                    role="button"
                    variant="primary"
                    size="lg"
                    onClick={() => {
                      if (showStep < totalSteps) {
                        // if (
                        //   showStep === 2 &&
                        //   selectedType === "delivery" &&
                        //   !validatePostalCode()
                        // ) {
                        //   return;
                        // }
                        setShowStep(showStep + 1);
                      } else {
                        if (
                          selectedType === "delivery" &&
                          validatePostalCode()
                        ) {
                          localStorage.setItem(
                            "deliveryPostalCodeValue",
                            deliveryPostalCodeValue
                          );

                          if (
                            restaurantDetail?.deliveryPostalCodes?.check_delivery_postal_codes?.includes(
                              deliveryPostalCodeValue
                            )
                          ) {
                            setIsOpen(true);
                            setOpen(true);
                            setRestaurantMessage(
                              restaurantMessage[currentLanguage.language] !== ""
                                ? true
                                : false
                            );
                          } else {
                            const filteredServices = orderingMethodData.filter(
                              (service) => service.value !== "delivery"
                            );
                            const serviceLabels = filteredServices
                              .map((service) => t(service.label))
                              .join(", "); // Transform labels to readable format

                            const message = t("delivery_postal_code.message")
                              .replace("{serviceLabels}", serviceLabels)
                              .replace("{postalCode}", deliveryPostalCodeValue); // Replacing serviceLabels placeholder and joining array element
                            toast({
                              title: t("delivery_postal_code.title"),
                              description: message,
                            });
                          }
                        } else {
                          setIsOpen(true);
                          setOpen(true);
                          setRestaurantMessage(
                            restaurantMessage[currentLanguage.language] !== ""
                              ? true
                              : false
                          );
                        }
                        setIsOpen(true);
                        setOpen(true);
                        setRestaurantMessage(
                          restaurantMessage[currentLanguage.language] !== ""
                            ? true
                            : false
                        );
                      }
                    }}
                    disabled={showStep === 2 && selectedType === ""}
                    label={showStep === totalSteps ? t("apply") : t("next")}
                    className={showStep === 1 ? "ml-auto" : ""}
                  />
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </React.Fragment>
  );
}
